import PageLayout from '@/components/layout/PageLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Mail, Phone, MapPin } from 'lucide-react';


const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    issue: '',
    referral: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post(`/form-submissions`, {
        form_template_id: 'contact-form',
        form_name: 'Contact Form',
        data: formData
      });

      toast.success('Message sent successfully! We will get back to you shortly.');
      setFormData({ name: '', phone: '', email: '', issue: '', referral: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout>

      <section className="py-20 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <h1 className="font-serif text-5xl md:text-6xl font-bold text-primary text-center mb-8" data-testid="contact-heading">
            Contact Us
          </h1>
          <p className="font-sans text-lg md:text-xl text-center text-muted-foreground mb-16 max-w-3xl mx-auto leading-relaxed">
            Instructing us is direct and simple. Fill in the contact form and we will be in touch with you shortly to book an intake.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white border border-border p-8 shadow-sm" data-testid="contact-form">
              <h2 className="font-serif text-3xl font-semibold text-primary mb-6">Get In Touch</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name" className="text-sm font-medium mb-2 block">
                    Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full"
                    data-testid="name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="text-sm font-medium mb-2 block">
                    Phone <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full"
                    data-testid="phone-input"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-sm font-medium mb-2 block">
                    Email <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full"
                    data-testid="email-input"
                  />
                </div>

                <div>
                  <Label htmlFor="issue" className="text-sm font-medium mb-2 block">
                    Description of Issue <span className="text-destructive">*</span>
                  </Label>
                  <Textarea
                    id="issue"
                    required
                    value={formData.issue}
                    onChange={(e) => setFormData({ ...formData, issue: e.target.value })}
                    className="w-full"
                    rows={5}
                    data-testid="issue-input"
                  />
                </div>

                <div>
                  <Label htmlFor="referral" className="text-sm font-medium mb-2 block">
                    How Did You Hear About Us
                  </Label>
                  <Input
                    id="referral"
                    type="text"
                    value={formData.referral}
                    onChange={(e) => setFormData({ ...formData, referral: e.target.value })}
                    className="w-full"
                    data-testid="referral-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-6 text-lg font-medium"
                  data-testid="submit-button"
                >
                  {loading ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </div>

            {/* Contact Information */}
            <div>
              <div className="bg-secondary p-8 border border-border mb-8">
                <h2 className="font-serif text-3xl font-semibold text-primary mb-6">Our Office</h2>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <MapPin className="text-accent mt-1" size={20} />
                    <div>
                      <p className="font-sans text-base text-primary font-medium">Address</p>
                      <p className="font-sans text-base text-muted-foreground">Toronto, Ontario</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Mail className="text-accent mt-1" size={20} />
                    <div>
                      <p className="font-sans text-base text-primary font-medium">Email</p>
                      <p className="font-sans text-base text-muted-foreground">admin@fortychambers.com</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Phone className="text-accent mt-1" size={20} />
                    <div>
                      <p className="font-sans text-base text-primary font-medium">Phone</p>
                      <p className="font-sans text-base text-muted-foreground">Available upon request</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-border p-8">
                <h3 className="font-serif text-2xl font-semibold text-primary mb-4">Advantages of Forty Chambers</h3>
                <ul className="space-y-3 font-sans text-base text-muted-foreground leading-relaxed">
                  <li className="flex items-start">
                    <span className="text-accent mr-2" aria-hidden="true">•</span>
                    <span>Cost-effective legal advice with quicker resolution of legal issues</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-accent mr-2" aria-hidden="true">•</span>
                    <span>Fixed fee basis or hourly rate with transparent engagement letters</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-accent mr-2" aria-hidden="true">•</span>
                    <span>Fees paid prior to carrying out specified work</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-accent mr-2" aria-hidden="true">•</span>
                    <span>Quick assessment of your case and whether we can assist</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-secondary p-8 border border-border">
              <h3 className="font-serif text-xl font-semibold text-primary mb-3">What is the scope of work offered?</h3>
              <p className="font-sans text-base text-muted-foreground leading-relaxed">
                We draft documents, review existing policies, and conduct legal research on your behalf.
              </p>
            </div>
            <div className="bg-secondary p-8 border border-border">
              <h3 className="font-serif text-xl font-semibold text-primary mb-3">Do you offer representation in court?</h3>
              <p className="font-sans text-base text-muted-foreground leading-relaxed">
                We do NOT offer representation at hearings. We provide legal advice and consultation services.
              </p>
            </div>
            <div className="bg-secondary p-8 border border-border">
              <h3 className="font-serif text-xl font-semibold text-primary mb-3">How quickly can you respond?</h3>
              <p className="font-sans text-base text-muted-foreground leading-relaxed">
                We assess your case and advise as quickly as possible if we can assist with your legal issue.
              </p>
            </div>
          </div>
        </div>
      </section>

    </PageLayout>
  );
};

export default Contact;